import { useMemo, useState } from "react";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import AnalyticsDataCompletionSchoolsModal from "@/components/AnalyticsDataCompletionSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import "../pages/AnalyticsDataCompletionTab.css";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");

const fmtL = (v) => {
  if (typeof v !== "number") return v ?? "—";
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return String(v);
};

const fmtK = (v) => {
  if (typeof v !== "number") return v ?? "—";
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return String(v);
};

const rateBadgeClass = (rate) => {
  if (rate < 80) return "adc-risk";
  if (rate >= 90) return "adc-good";
  return "";
};

const priorityTag = (rate, rank) => {
  if (rate < 80 || rank <= 2) return { label: "Critical", className: "adc-red" };
  if (rate < 83 || rank <= 5) return { label: "High", className: "" };
  return { label: "Watch", className: "adc-blue" };
};

const AnalyticsDataCompletionTab = ({
  completionData,
  loading = false,
  onRefresh,
  onGenerate,
  scopeLabel = "Pune District",
}) => {
  const [completionMetricModal, setCompletionMetricModal] = useState(null);
  const summary = completionData?.summary;
  const blocks = useMemo(() => completionData?.block_data || [], [completionData?.block_data]);

  const overallRate = summary?.overall_rate ?? 0;
  const pendingPct = Math.round((100 - overallRate) * 10) / 10;

  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0)),
    [blocks],
  );

  const top7 = useMemo(() => sortedBlocks.slice(0, 7), [sortedBlocks]);
  const top3Bottleneck = useMemo(() => top7.slice(0, 3).map((b) => b.block), [top7]);
  const topPerformers = useMemo(
    () => [...blocks].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0)).slice(0, 3).map((b) => b.block),
    [blocks],
  );

  const top5PendingShare = useMemo(() => {
    const totalPending = summary?.pending || 0;
    if (!totalPending) return 0;
    const top5Pending = sortedBlocks.slice(0, 5).reduce((sum, b) => sum + (b.pending || 0), 0);
    return Math.round((top5Pending / totalPending) * 1000) / 10;
  }, [sortedBlocks, summary]);

  const worstWeeks = useMemo(() => {
    const maxBlock = Math.max(...blocks.map((b) => b.estimated_weeks || 0), 0);
    return maxBlock || summary?.estimated_weeks_to_100 || 0;
  }, [blocks, summary]);

  const gaugeStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--adc-primary) 0 ${overallRate}%, rgba(37,99,235,.12) ${overallRate}% 100%)`,
    }),
    [overallRate],
  );

  const weeklyForecast = useMemo(() => {
    const base = overallRate;
    const weeks = ["Now", "W+1", "W+2", "W+3", "W+4", "W+5", "W+6"];
    const rates = weeks.map((_, i) => Math.min(100, Math.round((base + i * 2) * 10) / 10));
    const minRate = base;
    const maxRate = rates[rates.length - 1];
    const span = Math.max(maxRate - minRate, 1);
    return weeks.map((label, i) => {
      const rate = rates[i];
      const heightPct = Math.max(18, Math.round(((rate - minRate) / span) * 82) + 18);
      let barClass = "";
      if (i === 0) barClass = "adc-warning";
      else if (i >= 5) barClass = "adc-success";
      return { label, rate, heightPct, barClass };
    });
  }, [overallRate]);

  const highPendingBlocks = useMemo(
    () => blocks.filter((b) => (b.pending || 0) > 50000).slice(0, 2).map((b) => b.block),
    [blocks],
  );

  const defaultRoot = useMemo(() => {
    const worst = sortedBlocks[0];
    const pendingNames = highPendingBlocks.length >= 2
      ? highPendingBlocks.join(" and ")
      : highPendingBlocks[0] || top7[1]?.block || "priority blocks";
    return [
      worst
        ? `${worst.block} has the lowest completion rate at ${worst.rate}% with ${worst.estimated_weeks} weeks to closure.`
        : "Identify the lowest completion-rate block for immediate follow-up.",
      `District completion is ${overallRate}% with ${fmt(summary?.pending)} pending records (${pendingPct}% remaining).`,
      `${pendingNames} carry the highest pending record volumes and need focused resourcing.`,
    ];
  }, [sortedBlocks, highPendingBlocks, top7, overallRate, summary, pendingPct]);

  const defaultRecs = useMemo(
    () => [
      `Prioritize ${top3Bottleneck.join(", ") || "bottleneck blocks"} because they combine low rate with high pending counts.`,
      "Assign 2–3 week catch-up targets for blocks below 82% completion.",
      `${topPerformers.join(", ") || "Top performers"} can be used as process benchmarks for slower blocks.`,
    ],
    [top3Bottleneck, topPerformers],
  );

  const defaultActions = useMemo(
    () => [
      `Create daily exception list for pending records in ${top3Bottleneck[0] || "priority blocks"}.`,
      "Review block-wise closures every 48 hours until district crosses 92%.",
      `Protect momentum in ${topPerformers[0] || "high-performing blocks"} while replicating their process.`,
    ],
    [top3Bottleneck, topPerformers],
  );

  if (loading && !completionData) {
    return (
      <div className="analytics-completion-view" data-testid="analytics-completion-tab">
        <div className="adc-loading">
          <div className="adc-loading-spinner" />
          <span>Generating data completion forecast…</span>
        </div>
      </div>
    );
  }

  if (!completionData) {
    return (
      <div className="analytics-completion-view" data-testid="analytics-completion-tab">
        <div className="adc-loading">
          <p style={{ margin: 0, fontSize: 18, color: "var(--adc-text)", fontWeight: 900 }}>Data Completion Command Centre</p>
          <p style={{ margin: 0, maxWidth: 420, textAlign: "center" }}>
            APAAR/Aadhaar completion forecast and acceleration strategy.
          </p>
          <button type="button" className="adc-btn adc-btn-primary" onClick={onGenerate}>
            Generate Timeline
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-completion-view" data-testid="analytics-completion-tab">
      <section className="adc-page-header">
        <div>
          <div className="adc-eyebrow">
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v18" />
              <path d="M3 12h18" />
            </svg>
            AI-powered predictions · {scopeLabel} · 2025–26
          </div>
          <h1>Data Completion Command Centre</h1>
          <p>
            Track student record generation progress, identify bottleneck blocks, forecast time-to-completion, and
            trigger targeted follow-ups from one clear operational view.
          </p>
        </div>
        <div className="adc-header-actions">
          <button type="button" className="adc-btn" title="Export">
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          </button>
          <button type="button" className="adc-btn adc-btn-primary" onClick={onRefresh} disabled={loading}>
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <section className="adc-hero-card">
        <div className="adc-hero-layout">
          <div>
            <div className="adc-command-main">
              <div className="adc-gauge" style={gaugeStyle} aria-label={`Data completion rate ${overallRate} percent`}>
                <div>
                  <strong>{overallRate}%</strong>
                  <span>Complete</span>
                </div>
              </div>
              <div className="adc-command-copy">
                <h2 className="dashboard-kpi-hero-title">
                  {overallRate >= 85 ? "Completion is strong, but not yet safe" : "Completion needs focused follow-up"}
                </h2>
                <p>
                  {fmtL(summary?.apaar_generated)} of {fmtL(summary?.total_students)} student records are generated. The
                  remaining {fmtK(summary?.pending)} records are concentrated in a few slow blocks and can delay
                  district closure by up to {worstWeeks} weeks.
                </p>
                <div className="adc-status-row">
                  <HeroStatusChip
                    chipClass="adc-status-chip"
                    variant="adc-green"
                    onClick={() => setCompletionMetricModal("generated")}
                    ariaLabel={`${fmtL(summary?.apaar_generated)} generated — view schools below full completion`}
                  >
                    {fmtL(summary?.apaar_generated)} Generated
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="adc-status-chip"
                    variant="adc-orange"
                    onClick={() => setCompletionMetricModal("pending")}
                    ariaLabel={`${fmtK(summary?.pending)} pending — view schools with outstanding records`}
                  >
                    {fmtK(summary?.pending)} Pending
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="adc-status-chip"
                    onClick={() => setCompletionMetricModal("worst_case_closure")}
                    ariaLabel={`${summary?.estimated_weeks_to_100} week base forecast — view schools with longest timeline`}
                  >
                    {summary?.estimated_weeks_to_100} Week Base Forecast
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="adc-summary-note">
              <div className="adc-icon-box adc-purple">
                <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="m19 9-5 5-4-4-3 3" />
                </svg>
              </div>
              <div>
                <b>Primary action</b>
                <span>
                  Assign weekly closure targets to {top3Bottleneck.join(", ") || "priority blocks"}; these blocks carry
                  the largest pending load and longest completion timeline.
                </span>
              </div>
            </div>
          </div>

          <div className="adc-metric-grid" aria-label="Data completion KPIs">
            <article className="adc-metric-card">
              <small>Total Students</small>
              <strong>{fmtL(summary?.total_students)}</strong>
              <span>Eligible student records</span>
            </article>
            <article className="adc-metric-card adc-green">
              <small>Generated</small>
              <strong>{fmtL(summary?.apaar_generated)}</strong>
              <span>Records completed</span>
            </article>
            <article className="adc-metric-card adc-purple">
              <small>Completion Rate</small>
              <strong>{overallRate}%</strong>
              <span>District-level progress</span>
            </article>
            <article className="adc-metric-card adc-orange">
              <small>Pending Records</small>
              <strong>{fmtK(summary?.pending)}</strong>
              <span>Requires block follow-up</span>
            </article>
          </div>
        </div>
      </section>

      <div className="adc-section-title" id="diagnostics">
        <div>
          <h2>Completion Diagnostics</h2>
          <p>
            Key indicators are separated into progress, pending load and forecast sections so officers can understand
            what is complete, what is pending and where to intervene.
          </p>
        </div>
        <button type="button" className="adc-section-action" onClick={() => document.getElementById("blocks")?.scrollIntoView({ behavior: "smooth" })}>
          View bottleneck blocks →
        </button>
      </div>

      <section className="adc-analytics-grid">
        <article className="adc-panel">
          <div className="adc-panel-head">
            <div>
              <h3>Block Completion Snapshot</h3>
              <p>Fast scan of overall progress and bottleneck pressure.</p>
            </div>
            <span className="adc-pill">Current status</span>
          </div>
          <div className="adc-progress-stack">
            <div
              className="adc-progress-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Generated: view schools below full APAAR completion"
              onClick={() => setCompletionMetricModal("generated")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCompletionMetricModal("generated");
                }
              }}
            >
              <b>Generated</b>
              <div className="adc-track">
                <div className="adc-fill adc-green" style={{ width: `${overallRate}%` }} />
              </div>
              <strong>{overallRate}%</strong>
            </div>
            <div
              className="adc-progress-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Pending: view schools with outstanding APAAR records"
              onClick={() => setCompletionMetricModal("pending")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCompletionMetricModal("pending");
                }
              }}
            >
              <b>Pending</b>
              <div className="adc-track">
                <div className="adc-fill adc-orange" style={{ width: `${pendingPct}%` }} />
              </div>
              <strong>{pendingPct}%</strong>
            </div>
            <div
              className="adc-progress-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Top-5 Pending Load: view schools in bottleneck blocks"
              onClick={() => setCompletionMetricModal("top5_pending_load")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCompletionMetricModal("top5_pending_load");
                }
              }}
            >
              <b>Top-5 Pending Load</b>
              <div className="adc-track">
                <div className="adc-fill adc-purple" style={{ width: `${top5PendingShare}%` }} />
              </div>
              <strong>{top5PendingShare}%</strong>
            </div>
            <div
              className="adc-progress-card clickable"
              role="button"
              tabIndex={0}
              aria-label="Worst-case Closure: view schools with longest estimated timeline"
              onClick={() => setCompletionMetricModal("worst_case_closure")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setCompletionMetricModal("worst_case_closure");
                }
              }}
            >
              <b>Worst-case Closure</b>
              <div className="adc-track">
                <div className="adc-fill" style={{ width: `${Math.min(100, (worstWeeks / 14) * 100)}%` }} />
              </div>
              <strong>{worstWeeks} weeks</strong>
            </div>
          </div>
        </article>

        <article className="adc-panel">
          <div className="adc-panel-head">
            <div>
              <h3>Weekly Completion Forecast</h3>
              <p>Expected progress if the current 2% weekly completion assumption holds.</p>
            </div>
            <span className="adc-pill adc-warning">Forecast</span>
          </div>
          <div className="adc-weekly-chart" aria-label="Weekly data completion forecast chart">
            {weeklyForecast.map((week) => (
              <div className="adc-week" key={week.label}>
                <div className="adc-bar-wrap">
                  <div className={`adc-vbar ${week.barClass}`} style={{ height: `${week.heightPct}%` }} />
                </div>
                <strong>{week.rate}%</strong>
                <span>{week.label}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <div className="adc-section-title" id="actions">
        <div>
          <h2>AI-Powered Action Queue</h2>
          <p>The long paragraph insight is converted into three action cards with ownership, urgency and expected outcome.</p>
        </div>
        <button type="button" className="adc-section-action" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Back to top ↑
        </button>
      </div>

      <section className="adc-action-grid">
        <article className="adc-action-card">
          <div className="adc-icon-box adc-danger">
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <h3>Close Bottleneck Blocks First</h3>
          <p>
            Prioritize {top3Bottleneck.join(", ") || "priority blocks"} because they combine low rate with high pending
            records.
          </p>
          <div className="adc-action-meta">
            <span className="adc-tag adc-red">High Priority</span>
            <span className="adc-tag adc-blue">Block Officer</span>
          </div>
        </article>
        <article className="adc-action-card">
          <div className="adc-icon-box adc-orange">
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <h3>Set Weekly Targets</h3>
          <p>Assign 2–3 week catch-up targets for blocks below 82% completion and review progress every Monday.</p>
          <div className="adc-action-meta">
            <span className="adc-tag">Time-bound</span>
            <span className="adc-tag adc-blue">Weekly Review</span>
          </div>
        </article>
        <article className="adc-action-card">
          <div className="adc-icon-box adc-green">
            <svg className="adc-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <h3>Protect High Performers</h3>
          <p>
            Maintain momentum in {topPerformers.join(", ") || "top blocks"}; use their process as a playbook for slower
            blocks.
          </p>
          <div className="adc-action-meta">
            <span className="adc-tag adc-green">Best Practice</span>
            <span className="adc-tag adc-blue">Replication</span>
          </div>
        </article>
      </section>

      <div className="adc-section-title" id="blocks">
        <div>
          <h2>Block Completion Status</h2>
          <p>
            Rankings combine rate, pending count and estimated weeks so the user can act on the true bottleneck instead
            of only the lowest percentage.
          </p>
        </div>
        <button type="button" className="adc-section-action" onClick={() => document.getElementById("actions")?.scrollIntoView({ behavior: "smooth" })}>
          Review actions →
        </button>
      </div>

      <section className="adc-two-column">
        <article className="adc-panel adc-table-card">
          <div className="adc-table-head">
            <div>
              <h3>Bottleneck Block Ranking</h3>
              <p>Sorted by lowest completion rate and operational delay risk.</p>
            </div>
            <span className="adc-pill adc-danger">Needs follow-up</span>
          </div>
          <table className="adc-table" aria-label="Block-wise data completion status">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Completion</th>
                <th>Pending</th>
                <th>Est. Weeks</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {top7.map((block, index) => {
                const tag = priorityTag(block.rate, index + 1);
                return (
                  <tr key={block.block}>
                    <td>{index + 1}</td>
                    <td>{block.block}</td>
                    <td>
                      <span className={`adc-rate-badge ${rateBadgeClass(block.rate)}`}>{block.rate}%</span>
                    </td>
                    <td className="adc-score">{fmt(block.pending)}</td>
                    <td>{block.estimated_weeks}</td>
                    <td>
                      <span className={`adc-tag ${tag.className}`}>{tag.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </article>

        <InterventionActionPlan
          aiContent={completionData?.ai_insights}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <AnalyticsDataCompletionSchoolsModal
        open={Boolean(completionMetricModal)}
        metric={completionMetricModal}
        onClose={() => setCompletionMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={
          completionMetricModal === "generated"
            ? overallRate
            : completionMetricModal === "pending"
              ? pendingPct
              : completionMetricModal === "top5_pending_load"
                ? top5PendingShare
                : completionMetricModal === "worst_case_closure"
                  ? worstWeeks
                  : null
        }
      />
    </div>
  );
};

export default AnalyticsDataCompletionTab;
